import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { labService } from '../services';
import { LabSummary } from '../types';
import { LoadingSpinner, Card, Badge, Button, Alert } from '../components/ui';

export function Labs() {
  const [labs, setLabs] = useState<LabSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadLabs();
  }, []);

  const loadLabs = async () => {
    try {
      setLoading(true);
      setError(null);
      const labsData = await labService.getLabs();
      setLabs(labsData);
    } catch (err) {
      console.error('Failed to load labs:', err);
      setError('Failed to load labs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'beginner':
        return 'bg-green-500';
      case 'intermediate':
        return 'bg-yellow-500';
      case 'advanced':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const handleViewLab = (labSlug: string) => {
    navigate(`/labs/${labSlug}`);
  };

  if (loading) {
    return (
      <div className="p-8 text-white bg-gray-900 min-h-screen">
        <div className="flex items-center justify-center mt-20">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 text-white bg-gray-900 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-4">Lab Catalog</h1>
          <p className="text-gray-400">
            Choose from our collection of hands-on Linux training labs.
          </p>
        </div>

        {error && (
          <Alert variant="error" className="mb-6">
            {error}
            <Button 
              onClick={loadLabs} 
              className="ml-4"
              size="sm"
            >
              Retry
            </Button>
          </Alert>
        )}

        {labs.length === 0 && !loading && !error && (
          <Alert variant="info">
            No labs available. Contact your administrator.
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {labs.map((lab) => (
            <Card key={lab.slug} className="bg-gray-800 border-gray-700" data-testid="lab-card">
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {lab.title}
                    </h3>
                    <Badge 
                      className={`${getDifficultyColor(lab.difficulty)} text-white`}
                    >
                      {lab.difficulty}
                    </Badge>
                  </div>
                  <div className="text-sm text-gray-400">
                    {lab.estimated_minutes} min
                  </div>
                </div>

                {/* Summary */}
                <p className="text-gray-300 text-sm mb-4 line-clamp-3">
                  {lab.summary}
                </p>

                {/* Tags */}
                {lab.tags && lab.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {lab.tags.slice(0, 3).map((tag) => (
                      <Badge 
                        key={tag} 
                        variant="default"
                        size="sm"
                      >
                        {tag}
                      </Badge>
                    ))}
                    {lab.tags.length > 3 && (
                      <Badge 
                        variant="default"
                        size="sm"
                      >
                        +{lab.tags.length - 3} more
                      </Badge>
                    )}
                  </div>
                )}

                {/* Action */}
                <Button 
                  onClick={() => handleViewLab(lab.slug)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="view-lab-button"
                >
                  View Lab Details
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {/* Debug Info (only in development) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-8 p-4 bg-gray-800 rounded-lg">
            <h3 className="text-sm font-medium text-gray-400 mb-2">
              Debug Info ({labs.length} labs loaded):
            </h3>
            <pre className="text-xs text-gray-500 overflow-x-auto">
              {JSON.stringify(labs.map(lab => ({ 
                slug: lab.slug, 
                title: lab.title, 
                difficulty: lab.difficulty 
              })), null, 2)}
            </pre>
          </div>
        )}

      </div>
    </div>
  );
}
